import { useCallback, useEffect, useRef, useState } from 'react';
import { useCredits } from './use-credits';
import { useFreeUsageUntil } from './use-free-usage';
import { useWins } from './use-wins';
import { useBlackFridayCampaignEnabled } from './black-friday-flag';
import { useBlackFridaySimWindow } from './black-friday-sim';
import {
  BLACK_FRIDAY_WINS_RETRIES,
  blackFridayBannerState,
  hasWinsInWindow,
  isBlackFridayCountdown,
  type BlackFridayBannerState,
} from './black-friday';
import {
  BLACK_FRIDAY_RESET_EVENT,
  blackFridayEndedDismissed,
  blackFridayModalSeen,
  blackFridayOfferDismissed,
  blackFridaySeenUntil,
  blackFridayWinsOpened,
  markBlackFridayEndedDismissed,
  markBlackFridayModalSeen,
  markBlackFridayOfferDismissed,
  markBlackFridayWinsOpened,
  rememberBlackFridayUntil,
} from './black-friday-storage';

export interface BlackFridayView {
  /** Which of the five bands to draw, or `hidden`. */
  state: BlackFridayBannerState;
  /** The open window's end (ISO) — the countdown's target. Null unless `running` / `final`. */
  untilIso: string | null;
  /** Whether the one-time offer modal should open now. Web only; there is no modal on the phones. */
  showModal: boolean;
  /**
   * The band's X. What it means depends on which band is showing, deliberately:
   *
   * - **offer** and the two **over** bands — persisted, so one close ends it, as asked.
   * - the two **countdowns** — this session only, so the band goes for the visit and comes back. A
   *   countdown is live information about an account that is being charged nothing, and a permanent
   *   close would also have to be undone when the window ends for the over-band to be reachable.
   */
  dismiss: () => void;
  /** The wins band's CTA. Persisted: from then on the over-band is "See plans", until its X. */
  openWins: () => void;
  /** Close the modal and never offer it again in this browser. */
  closeModal: () => void;
}

/**
 * Everything the Black Friday band and modal need, composed from the campaign spec, the credits
 * overview and what this browser remembers.
 *
 * Every client-only signal starts at its hidden value and resolves in an effect, so SSR and the first
 * paint never flash a band — the same rule `use-app-banner.ts` follows.
 */
export function useBlackFriday(): BlackFridayView {
  const campaignOn = useBlackFridayCampaignEnabled();
  // Neither the band nor the modal waits for onboarding: both show from the first screen, as soon as the
  // account is read. The modal opening over the onboarding chat is deliberate — new users are who the
  // offer is for, and it is once per browser.

  const credits = useCredits();
  // The credits read has SETTLED: a window we have not looked for yet is not a window that is absent,
  // and offering 48 free hours to someone whose hours are already running reads as broken.
  const sim = useBlackFridaySimWindow();
  // A sim answers for the whole account, the read included (the rule all three surfaces state) —
  // otherwise forcing a band on a browser whose credits never settle shows nothing.
  const accountKnown =
    sim.sim !== null || (!credits.loading && !credits.failed && credits.hasMetaSite !== false && !!credits.account);
  const freeUsageUntilIso = useFreeUsageUntil();

  // A wins click this visit counts even when storage refuses the write (private mode, blocked storage).
  const winsOpenedHere = useRef(false);

  // Persisted state, read in an effect and mirrored into React state so a dismissal repaints at once.
  const [storage, setStorage] = useState({
    offerDismissed: false,
    endedDismissed: false,
    winsOpened: false,
    modalSeen: true,
    seenUntil: null as string | null,
  });
  const readStorage = useCallback(() => {
    // Keeps the SAME object when nothing it reads has changed. This is read from an effect, and a fresh
    // object every time is a re-render every time — which is a loop as soon as anything upstream of that
    // effect also moves per render.
    setStorage((previous) => {
      const next = {
        offerDismissed: blackFridayOfferDismissed(),
        endedDismissed: blackFridayEndedDismissed(),
        winsOpened: blackFridayWinsOpened() || winsOpenedHere.current,
        modalSeen: blackFridayModalSeen(),
        seenUntil: blackFridaySeenUntil(),
      };
      const same =
        previous.offerDismissed === next.offerDismissed &&
        previous.endedDismissed === next.endedDismissed &&
        previous.winsOpened === next.winsOpened &&
        previous.modalSeen === next.modalSeen &&
        previous.seenUntil === next.seenUntil;
      return same ? previous : next;
    });
  }, []);
  useEffect(() => {
    readStorage();
    const onReset = () => {
      winsOpenedHere.current = false;
      readStorage();
    };
    window.addEventListener(BLACK_FRIDAY_RESET_EVENT, onReset);
    return () => window.removeEventListener(BLACK_FRIDAY_RESET_EVENT, onReset);
  }, [readStorage]);

  // The one write: an OPEN window is remembered, because the credits overview stops reporting it the
  // moment it closes and nothing else can tell the over-bands that a window ever ran. See `black-friday.ts`.
  //
  // NEVER under the simulator. Its window is a made-up deadline, and writing one would leave this
  // browser's real memory holding a window the account never had — which, once the simulation is off,
  // suppresses the modal and can resolve a real reader to an over-band.
  //
  // And never while the CAMPAIGN is off. Free-usage hours are not this campaign's alone, so a reader
  // outside the experiment who redeems any other promo would otherwise have the campaign's memory
  // written on their device — and the day the experiment reaches them, their first contact with it
  // would be "Your 48 free hours are over" for a window that was never Black Friday's. The effect
  // re-runs when the flag lands (it is a dependency), so a reader the campaign IS on for still has an
  // open window remembered on the turn the gate opens.
  const simulating = sim.sim !== null;
  useEffect(() => {
    if (!freeUsageUntilIso || simulating || !campaignOn) return;
    rememberBlackFridayUntil(freeUsageUntilIso);
    readStorage();
  }, [freeUsageUntilIso, simulating, campaignOn, readStorage]);

  // "Has this account ever had its 48 hours" — the ACCOUNT's answer first, this device's memory second.
  //
  // The server's `lastFreeUsageUntil` is the whole window, account-wide, so every device gets the
  // over-band and none of them ever offers hours that were already spent. The remembered value stays
  // underneath it for the two cases the field cannot cover: a BFF that predates it, and the moment
  // between redeeming here and the next credits read landing.
  const accountSeenUntil = credits.account?.lastFreeUsageUntilIso ?? null;
  const seenUntil = sim.sim ? sim.seenUntilIso : accountSeenUntil ?? storage.seenUntil;

  // Resolved WITHOUT wins first, because wins only pick between the two over-bands — so the list is
  // fetched once the band is already known to be one of those, and never for anyone else.
  const input = {
    campaignOn,
    accountKnown,
    freeUsageUntilIso,
    seenUntilIso: seenUntil,
    offerDismissed: storage.offerDismissed,
    endedDismissed: storage.endedDismissed,
    winsOpened: storage.winsOpened,
    now: Date.now(),
  };
  const base = blackFridayBannerState({ ...input, hasWindowWins: false });

  // A simulated band already answers the wins question, so it reads nothing. Once the wins were opened
  // from the band the answer no longer matters, so neither does the read. Three attempts in all, the
  // same budget as the phones.
  const { wins } = useWins(base === 'ended-plans' && !sim.sim && !storage.winsOpened, BLACK_FRIDAY_WINS_RETRIES);
  const hasWindowWins = sim.sim ? sim.hasWins : hasWinsInWindow(wins.map((w) => w.createdDate), seenUntil);
  const resolved = base === 'ended-plans' ? blackFridayBannerState({ ...input, hasWindowWins }) : base;

  // The countdowns' X closes the band for this visit only. Kept HERE rather than inside the banner so
  // the frame sees `hidden` and hands the top of the screen back to the bands this one outranks —
  // a band that returns null while the frame still believes it is showing keeps them all suppressed.
  // Keyed on the SIMULATION, so picking another countdown in Dev Tools previews it instead of meeting a
  // latch the previous preview set. Outside the simulator there is one window, so the key never moves.
  const [countdownClosed, setCountdownClosed] = useState(false);
  const closedFor = useRef(sim.sim);
  if (closedFor.current !== sim.sim) {
    closedFor.current = sim.sim;
    if (countdownClosed) setCountdownClosed(false);
  }
  const state: BlackFridayBannerState = countdownClosed && isBlackFridayCountdown(resolved) ? 'hidden' : resolved;

  // Re-render every second while a countdown is on screen. Here rather than in the frame, so only the
  // band repaints — the same choice `usePersonalSaleCountdown` makes.
  const [, setTick] = useState(0);
  const ticking = isBlackFridayCountdown(state);
  useEffect(() => {
    if (!ticking) return undefined;
    const timer = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(timer);
  }, [ticking]);

  const dismiss = useCallback(() => {
    if (isBlackFridayCountdown(resolved)) {
      setCountdownClosed(true);
      return;
    }
    if (resolved === 'offer') markBlackFridayOfferDismissed();
    else markBlackFridayEndedDismissed();
    readStorage();
  }, [resolved, readStorage]);
  const openWins = useCallback(() => {
    winsOpenedHere.current = true;
    markBlackFridayWinsOpened();
    readStorage();
  }, [readStorage]);
  const closeModal = useCallback(() => {
    markBlackFridayModalSeen();
    readStorage();
  }, [readStorage]);

  // The modal is the offer's own surface, so it opens only where the offer stands: the campaign is on,
  // the account has been read, no window is running and none ever has. It is
  // deliberately NOT gated on the band's dismissal — closing the band is not an answer to a modal the
  // reader has never been shown.
  const showModal = campaignOn && accountKnown && !storage.modalSeen && !freeUsageUntilIso && !seenUntil;

  return { state, untilIso: freeUsageUntilIso, showModal, dismiss, openWins, closeModal };
}
